import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ReviewsSection, StarRating } from '../components/ReviewsSection';
import { 
  MapPin, 
  Phone, 
  Globe, 
  Mail, 
  Building2, 
  ArrowLeft,
  Loader2,
  Eye,
  ExternalLink,
  User
} from 'lucide-react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const orangeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfessionalProfilePage = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const { isProfessional, hasPaid, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [professional, setProfessional] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    // Free model: anyone authenticated can view profiles (data masking is server-side)

    const fetchProfessional = async () => {
      try {
        const response = await axios.get(`${API}/professionals/${id}`);
        setProfessional(response.data);
      } catch (error) {
        console.error('Error fetching professional:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfessional();
  }, [id, hasPaid, navigate]);

  const getSpecBadgeColor = (spec) => {
    const colors = {
      mechatronics: 'bg-orange-100 text-orange-700 border-orange-200',
      mechanical: 'bg-blue-100 text-blue-700 border-blue-200',
      electrical: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      software: 'bg-green-100 text-green-700 border-green-200',
    };
    return colors[spec] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!professional) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Professional not found</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => navigate('/directory')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Directory
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="professional-profile-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/directory')}
          data-testid="back-to-directory"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('nav_directory')}
        </Button>
        
        {/* Main Card */}
        <Card className="border border-slate-200 rounded-md">
          <CardContent className="p-8">
            {/* Header with Logo */}
            <div className="flex flex-col md:flex-row md:items-start gap-6 mb-8">
              {/* Logo */}
              <div className="flex-shrink-0">
                {professional.logo_url ? (
                  <img 
                    src={professional.logo_url} 
                    alt={professional.company_name}
                    className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-md border border-slate-200"
                  />
                ) : (
                  <div className="w-24 h-24 md:w-32 md:h-32 bg-slate-100 rounded-md flex items-center justify-center">
                    {professional.entity_type === 'freelancer' ? (
                      <User className="h-12 w-12 text-slate-400" />
                    ) : (
                      <Building2 className="h-12 w-12 text-slate-400" />
                    )}
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-xs font-mono text-slate-400 uppercase tracking-wider">
                    ID: {professional.id.slice(0, 8)}
                  </span>
                  <Badge variant="outline" className={`text-xs ${
                    professional.entity_type === 'freelancer' 
                      ? 'bg-purple-100 text-purple-700 border-purple-200'
                      : 'bg-slate-100 text-slate-700 border-slate-200'
                  }`}>
                    {t(`entity_${professional.entity_type || 'company'}`)}
                  </Badge>
                </div>
                
                <h1 
                  className="text-3xl font-semibold text-slate-900 mt-2"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  data-testid="company-name"
                >
                  {professional.company_name}
                </h1>
                <p className="text-lg text-slate-600 mt-1">{professional.name}</p>
                
                {/* Rating */}
                <div className="flex items-center gap-2 mt-3">
                  <StarRating rating={Math.round(professional.average_rating || 0)} readOnly size="sm" />
                  <span className="text-slate-600">
                    {(professional.average_rating || 0).toFixed(1)} ({professional.review_count || 0} {t('dash_reviews').toLowerCase()})
                  </span>
                </div>
                
                {/* Stats */}
                <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                  <div className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    <span>{professional.profile_views} views</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">Zona</p>
                    <p className="text-slate-900">{professional.location_city || professional.city}{professional.country ? `, ${professional.country}` : ''}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">{t('pro_phone')}</p>
                    <a 
                      href={`tel:${professional.phone}`}
                      className="text-slate-900 hover:text-orange-500"
                      data-testid="phone-link"
                    >
                      {professional.phone}
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">{t('auth_email')}</p>
                    <a 
                      href={`mailto:${professional.email}`}
                      className="text-slate-900 hover:text-orange-500"
                      data-testid="email-link"
                    >
                      {professional.email}
                    </a>
                  </div>
                </div>
                
                {professional.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">{t('pro_website')}</p>
                      <a 
                        href={professional.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-900 hover:text-orange-500 flex items-center gap-1"
                        data-testid="website-link"
                      >
                        {professional.website}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Location Map */}
            {professional.approximate_lat && professional.approximate_lng && (
              <div className="mb-8" data-testid="profile-location-map">
                <h3 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Zona approssimata
                </h3>
                <div className="rounded-md overflow-hidden border border-slate-200" style={{ height: '250px' }}>
                  <MapContainer
                    center={[professional.approximate_lat, professional.approximate_lng]}
                    zoom={11}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                    dragging={false}
                    zoomControl={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker 
                      position={[professional.approximate_lat, professional.approximate_lng]} 
                      icon={orangeIcon}
                    />
                    <Circle
                      center={[professional.approximate_lat, professional.approximate_lng]}
                      radius={2000}
                      pathOptions={{ color: '#f97316', fillColor: '#fed7aa', fillOpacity: 0.3, weight: 2 }}
                    />
                  </MapContainer>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Posizione approssimata (~2km). L'indirizzo esatto non viene mostrato.
                </p>
              </div>
            )}
            
            {/* Description */}
            {professional.description && (
              <div className="mb-8">
                <h3 className="text-sm font-medium text-slate-500 mb-2">{t('pro_description')}</h3>
                <p className="text-slate-700 leading-relaxed">{professional.description}</p>
              </div>
            )}
            
            {/* Specializations */}
            <div className="mb-8">
              <h3 className="text-sm font-medium text-slate-500 mb-3">{t('pro_specializations')}</h3>
              <div className="space-y-4">
                {Object.entries(professional.specializations || {}).map(([spec, subs]) => (
                  <div key={spec} className="border border-slate-200 rounded-md p-4">
                    <Badge 
                      variant="outline"
                      className={`text-sm mb-3 ${getSpecBadgeColor(spec)}`}
                    >
                      {t(`spec_${spec}`)}
                    </Badge>
                    <div className="flex flex-wrap gap-2">
                      {subs.map((sub) => (
                        <span 
                          key={sub}
                          className="text-sm text-slate-600 bg-slate-100 px-2 py-1 rounded"
                        >
                          {t(`sub_${sub}`)}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Certifications */}
            {professional.certifications && Object.keys(professional.certifications).length > 0 && (
              <div className="mb-8" data-testid="certifications-section">
                <h3 className="text-sm font-medium text-slate-500 mb-3">{t('cert_title')}</h3>
                <div className="space-y-4">
                  {Object.entries(professional.certifications).map(([category, certs]) => (
                    <div key={category} className="border border-slate-200 rounded-md p-4">
                      <Badge 
                        variant="outline"
                        className="text-sm mb-3 bg-amber-100 text-amber-700 border-amber-200"
                      >
                        {t(`cert_${category}`)}
                      </Badge>
                      <div className="flex flex-wrap gap-2">
                        {certs.map((cert) => (
                          <span 
                            key={cert}
                            className="text-sm text-slate-600 bg-slate-100 px-2 py-1 rounded"
                          >
                            {t(`cert_${cert}`)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Regions */}
            <div className="mb-8">
              <h3 className="text-sm font-medium text-slate-500 mb-3">{t('pro_regions')}</h3>
              <div className="flex flex-wrap gap-2">
                {(professional.regions || []).map((region) => (
                  <Badge 
                    key={region}
                    variant="outline"
                    className="text-sm"
                  >
                    <Globe className="h-3 w-3 mr-1" />
                    {t(`region_${region}`)}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Contact Button */}
            {isProfessional && (
              <Button 
                className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
                onClick={() => navigate(`/messages?to=${professional.user_id}`)}
                data-testid="contact-button"
              >
                {t('dir_contact')}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Reviews Section */}
        <ReviewsSection 
          professionalId={professional.id}
          averageRating={professional.average_rating || 0}
          reviewCount={professional.review_count || 0}
        />
      </div>
    </div>
  );
};

export default ProfessionalProfilePage;
